import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { User, Storefront, MapPin, IdentificationCard, ArrowRight, SignOut } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CompleteProfilePage = () => {
  const navigate = useNavigate();
  const { api, user, refreshUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  const [profile, setProfile] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    mobile: user?.mobile || '',
    alternate_mobile: '',
    residence_address: '',
    pincode: '',
    aadhar_number: '',
    pan_number: ''
  });

  const [shop, setShop] = useState({
    gst_number: '',
    shop_name: '',
    shop_address: '',
    shop_pincode: '',
    state: ''
  });

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleSubmit = async () => {
    if (!profile.full_name || !profile.residence_address || !profile.pincode) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!shop.gst_number || !shop.shop_name || !shop.shop_address || !shop.shop_pincode) {
      toast.error('Please fill all shop details');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/complete-profile', {
        profile,
        shop
      });
      toast.success('Profile completed successfully');
      await refreshUser();
      navigate('/kyc-upload', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 pb-safe">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-4 py-6">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold font-outfit text-neutral-900">
                Complete Your Profile
              </h1>
              <p className="text-neutral-500 mt-1">
                Step {step} of 2 - {step === 1 ? 'Personal Details' : 'Shop Details'}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-red-600 transition-colors px-3 py-2 rounded-lg hover:bg-red-50"
              data-testid="logout-button"
            >
              <SignOut size={18} />
              Logout
            </button>
          </div>

          {/* Progress */}
          <div className="flex gap-2 mt-4">
            <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-red-600' : 'bg-neutral-200'}`} />
            <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-red-600' : 'bg-neutral-200'}`} />
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-lg mx-auto px-4 py-6">
        {step === 1 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
            <div className="flex items-center gap-3 text-red-600 mb-2">
              <User size={24} weight="fill" />
              <h2 className="text-lg font-semibold font-outfit">Personal Details</h2>
            </div>

            <div>
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={profile.full_name}
                onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                placeholder="Enter your full name"
                className="mt-1.5 h-12"
                data-testid="full-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                placeholder="your@email.com"
                className="mt-1.5 h-12"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="mobile">Mobile Number</Label>
              <Input
                id="mobile"
                value={profile.mobile}
                readOnly
                className="mt-1.5 h-12 bg-neutral-50"
                data-testid="mobile-display"
              />
            </div>

            <div>
              <Label htmlFor="alternate_mobile">Alternate Mobile (Optional)</Label>
              <Input
                id="alternate_mobile"
                value={profile.alternate_mobile}
                onChange={(e) => setProfile({ ...profile, alternate_mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                placeholder="Enter alternate number"
                className="mt-1.5 h-12"
                data-testid="alternate-mobile-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="aadhar_number">Aadhar Card Number</Label>
                <Input id="aadhar_number" value={profile.aadhar_number} onChange={(e) => setProfile({ ...profile, aadhar_number: e.target.value.replace(/\D/g, '').slice(0, 12) })} placeholder="12-digit Aadhar number" className="mt-1.5 h-12" data-testid="aadhar-input" />
              </div>
              <div>
                <Label htmlFor="pan_number">PAN Card Number</Label>
                <Input id="pan_number" value={profile.pan_number} onChange={(e) => setProfile({ ...profile, pan_number: e.target.value.toUpperCase().slice(0, 10) })} placeholder="e.g. ABCDE1234F" className="mt-1.5 h-12" data-testid="pan-input" />
              </div>
            </div>


            <div>
              <Label htmlFor="residence_address">Residence Address *</Label>
              <Textarea
                id="residence_address"
                value={profile.residence_address}
                onChange={(e) => setProfile({ ...profile, residence_address: e.target.value })}
                placeholder="Enter your complete address"
                className="mt-1.5 min-h-[100px]"
                data-testid="residence-address-input"
              />
            </div>

            <div>
              <Label htmlFor="pincode">Pincode *</Label>
              <Input
                id="pincode"
                value={profile.pincode}
                onChange={(e) => setProfile({ ...profile, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                placeholder="Enter 6-digit pincode"
                className="mt-1.5 h-12"
                data-testid="pincode-input"
              />
            </div>

            <Button
              onClick={() => setStep(2)}
              disabled={!profile.full_name || !profile.residence_address || !profile.pincode}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white"
              data-testid="next-step-button"
            >
              Continue to Shop Details
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
            <div className="flex items-center gap-3 text-red-600 mb-2">
              <Storefront size={24} weight="fill" />
              <h2 className="text-lg font-semibold font-outfit">Shop Details</h2>
            </div>

            <div>
              <Label htmlFor="gst_number">GST Number *</Label>
              <div className="relative mt-1.5">
                <IdentificationCard size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  id="gst_number"
                  value={shop.gst_number}
                  onChange={(e) => setShop({ ...shop, gst_number: e.target.value.toUpperCase() })}
                  placeholder="e.g., 22AAAAA0000A1Z5"
                  className="pl-10 h-12"
                  data-testid="gst-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="shop_name">Shop Name *</Label>
              <Input
                id="shop_name"
                value={shop.shop_name}
                onChange={(e) => setShop({ ...shop, shop_name: e.target.value })}
                placeholder="Enter shop name"
                className="mt-1.5 h-12"
                data-testid="shop-name-input"
              />
            </div>

            <div>
              <Label htmlFor="shop_address">Shop Address *</Label>
              <Textarea
                id="shop_address"
                value={shop.shop_address}
                onChange={(e) => setShop({ ...shop, shop_address: e.target.value })}
                placeholder="Enter complete shop address"
                className="mt-1.5 min-h-[100px]"
                data-testid="shop-address-input"
              />
            </div>

            <div>
              <Label htmlFor="shop_pincode">Shop Pincode *</Label>
              <div className="relative mt-1.5">
                <MapPin size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  id="shop_pincode"
                  value={shop.shop_pincode}
                  onChange={(e) => setShop({ ...shop, shop_pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  placeholder="Enter 6-digit pincode"
                  className="pl-10 h-12"
                  data-testid="shop-pincode-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="shop_state">State *</Label>
              <select
                id="shop_state"
                value={shop.state}
                onChange={(e) => setShop({ ...shop, state: e.target.value })}
                className="mt-1.5 w-full rounded-md border border-neutral-200 px-3 py-3 text-sm bg-white h-12"
                data-testid="shop-state-select"
              >
                <option value="">Select State</option>
                <option value="Karnataka">Karnataka</option>
                <option value="Andhra Pradesh">Andhra Pradesh</option>
                <option value="Telangana">Telangana</option>
                <option value="Tamil Nadu">Tamil Nadu</option>
                <option value="Maharashtra">Maharashtra</option>
                <option value="Kerala">Kerala</option>
                <option value="Goa">Goa</option>
                <option value="Gujarat">Gujarat</option>
                <option value="Rajasthan">Rajasthan</option>
                <option value="Madhya Pradesh">Madhya Pradesh</option>
                <option value="Uttar Pradesh">Uttar Pradesh</option>
                <option value="Bihar">Bihar</option>
                <option value="West Bengal">West Bengal</option>
                <option value="Odisha">Odisha</option>
                <option value="Punjab">Punjab</option>
                <option value="Haryana">Haryana</option>
                <option value="Chhattisgarh">Chhattisgarh</option>
                <option value="Jharkhand">Jharkhand</option>
                <option value="Assam">Assam</option>
              </select>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 h-12 border-neutral-200"
                data-testid="back-step-button"
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !shop.gst_number || !shop.shop_name || !shop.shop_address || !shop.shop_pincode}
                className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white"
                data-testid="submit-profile-button"
              >
                {loading ? 'Saving...' : 'Save & Continue'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompleteProfilePage;
